// A board of the call on the stage (ADR-0007), as a screen shown is: its shapes on a canvas that fills its
// place, 16:9 and whole, and the others' pointers with their names. Above it, whose it is and who draws on
// it, a picture of it (PNG), and — for those who may — saying who draws, clearing and closing. Below it, for
// the user who may draw, the tools in one row: the hand (picks shapes and moves them; Delete erases them),
// the pen, the line, the arrow, the rectangle, the ellipse, the text, the eraser and the pointer; the colour
// — its palette over the row —, the widths; undo and redo (Ctrl+Z, Ctrl+Shift+Z). A finger draws on a phone:
// the page does not scroll or zoom under the board.
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  bounds,
  css,
  HEIGHT,
  hit,
  MOST_TEXT,
  paint,
  shifted,
  WIDTH,
  type Board,
  type Kind,
  type Shape,
} from "../board.ts";
import {
  addShape,
  ageBoards,
  clearBoard,
  closeBoard,
  drawing,
  eraseShapes,
  hosts,
  mayClose,
  mayDraw,
  moveShapes,
  newId,
  penDown,
  penTo,
  penUp,
  point,
  redo,
  setMode,
  shapesOf,
  undo,
} from "../boards.ts";
import { nameOf, state } from "../store.ts";
import { Icon, IconButton, type IconName } from "./Icon.tsx";

type Tool = "hand" | "pen" | "line" | "arrow" | "rect" | "ellipse" | "text" | "eraser" | "laser";

const TOOLS: [Tool, IconName, string][] = [
  ["hand", "hand", "Рука: выбрать и сдвинуть"],
  ["pen", "pencil", "Перо"],
  ["line", "line", "Линия"],
  ["arrow", "arrow", "Стрелка"],
  ["rect", "rect", "Прямоугольник"],
  ["ellipse", "ellipse", "Эллипс"],
  ["text", "text", "Текст"],
  ["eraser", "eraser", "Ластик"],
  ["laser", "laser", "Указка"],
];

/** The colours: ink, the brand's teal, blue, red, orange, green, violet. */
const COLORS = [0x1f2a30, 0x00b8ac, 0x2266ff, 0xe5484d, 0xf08c00, 0x2f9e44, 0x7048e8];
/** The widths of lines, a board's units, the size of a text that goes with each, and their names. */
const WIDTHS: [number, number, string][] = [
  [4, 32, "Тонкая линия, мелкий текст"],
  [8, 48, "Средняя линия и текст"],
  [16, 72, "Толстая линия, крупный текст"],
];
/** How near the eraser and the hand reach a shape's line, CSS pixels; how often the pointer goes, ms; the
 * least drag that makes a shape, a board's units. */
const REACH = 8;
const POINT_EVERY = 50;
const LEAST = 4;
/** A phone's screen, as style.css has it. */
const PHONE = "(max-width: 720px)";

/** What the pointer does on the board while it is pressed. */
type Drag =
  | { kind: "pen" }
  | { kind: "shape"; shape: Kind; x0: number; y0: number; x1: number; y1: number }
  | { kind: "move"; ids: string[]; x0: number; y0: number; dx: number; dy: number }
  | { kind: "erase"; erased: Set<string> };

/** The shape a drag of a shape tool makes, in colour `color` and width `width` (no id yet). */
function dragged(
  d: { shape: Kind; x0: number; y0: number; x1: number; y1: number },
  color: number,
  width: number,
): Shape {
  const points = Int16Array.of(d.x0, d.y0, d.x1, d.y1);
  return { id: "", kind: d.shape, points, color, width, text: "", size: 0, by: 0 };
}

export function BoardTile({ b }: { b: Board }) {
  const area = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const drag = useRef<Drag | null>(null);
  const pointed = useRef(0);
  const pointing = useRef(false);
  const typed = useRef(false);
  const [box, setBox] = useState<[number, number]>([0, 0]);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(0);
  const [palette, setPalette] = useState(false);
  const [typing, setTyping] = useState<{ x: number; y: number } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [, redraw] = useState(0);
  const draws = mayDraw(b);
  const shapes = shapesOf(b);
  // The shapes picked that are still there.
  const selected = picked.filter((id) => shapes.some((h) => h.id === id));

  // The canvas: as large as its place takes, 16:9 — on a phone as wide as the place, and the place as high
  // as the canvas, its tools right below it.
  useLayoutEffect(() => {
    const el = area.current!;
    const measure = () => {
      const narrow = window.matchMedia(PHONE).matches;
      const w = Math.floor(narrow ? el.clientWidth : Math.min(el.clientWidth, (el.clientHeight * WIDTH) / HEIGHT));
      const h = Math.floor((w * HEIGHT) / WIDTH);
      setBox((old) => (old[0] === w && old[1] === h ? old : [w, h]));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A text begun: the field takes the keys (`autoFocus` does not, in a page already shown).
  useLayoutEffect(() => {
    if (typing !== null) field.current?.focus({ preventScroll: true });
  }, [typing]);

  // The acts of the user's the server lost and the pointers that stopped go now and then.
  useEffect(() => {
    const t = setInterval(ageBoards, 1000);
    return () => clearInterval(t);
  }, []);

  // The pointer the user showed goes with the board.
  useEffect(() => () => unpoint(), [b.number]);

  // Drawn anew after every change: the shapes, what a drag makes, the shapes picked, the pointers.
  useLayoutEffect(() => {
    const c = canvas.current;
    if (c === null || box[0] === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const [pw, ph] = [Math.round(box[0] * dpr), Math.round(box[1] * dpr)];
    if (c.width !== pw || c.height !== ph) [c.width, c.height] = [pw, ph];
    const ctx = c.getContext("2d")!;
    const k = pw / WIDTH;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pw, ph);
    const d = drag.current;
    const moving = d?.kind === "move" ? new Set(d.ids) : null;
    const all =
      d?.kind === "move"
        ? shapes.map((h) => (moving!.has(h.id) ? { ...h, points: shifted(h.points, d.dx, d.dy) } : h))
        : shapes;
    paint(ctx, all, k);
    const own = drawing(b.number);
    if (own !== null) paint(ctx, [own], k);
    if (d?.kind === "shape") paint(ctx, [dragged(d, color, WIDTHS[width][0])], k);
    ctx.setLineDash([6 * dpr, 4 * dpr]);
    ctx.strokeStyle = "#00b8ac";
    ctx.lineWidth = 1.5 * dpr;
    for (const h of all) {
      if (!selected.includes(h.id)) continue;
      const [x0, y0, x1, y1] = bounds(h);
      ctx.strokeRect(x0 * k - 4 * dpr, y0 * k - 4 * dpr, (x1 - x0) * k + 8 * dpr, (y1 - y0) * k + 8 * dpr);
    }
    ctx.setLineDash([]);
    for (const [user, p] of b.pointers) {
      const [px, py] = [p.x * k, p.y * k];
      ctx.fillStyle = "rgba(229, 72, 77, 0.25)";
      ctx.beginPath();
      ctx.arc(px, py, 14 * dpr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = "#e5484d";
      ctx.beginPath();
      ctx.arc(px, py, 6 * dpr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.font = `${12 * dpr}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      const name = nameOf(user);
      const tw = ctx.measureText(name).width;
      ctx.fillStyle = "rgba(15, 28, 36, 0.78)";
      ctx.fillRect(px + 12 * dpr, py + 10 * dpr, tw + 10 * dpr, 18 * dpr);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(name, px + 17 * dpr, py + 13 * dpr);
    }
  });

  /** Where pointer event `e` is on the board, in its units. */
  const at = (e: PointerEvent): [number, number] => {
    const r = canvas.current!.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * WIDTH);
    return [x, Math.round(((e.clientY - r.top) / r.height) * HEIGHT)];
  };
  /** How near the eraser and the hand reach, a board's units. */
  const reach = () => (box[0] > 0 ? (REACH * WIDTH) / box[0] : REACH);
  const eraseAt = (x: number, y: number, erased: Set<string>) => {
    const h = hit(shapesOf(b), x, y, reach());
    if (h === null || erased.has(h.id)) return;
    erased.add(h.id);
    eraseShapes(b.number, [h.id]);
  };
  const pointAt = (x: number, y: number) => {
    const now = performance.now();
    if (now - pointed.current < POINT_EVERY) return;
    pointed.current = now;
    pointing.current = true;
    point(b.number, x, y);
  };
  /** The pointer the user showed goes: the others see it no more. */
  function unpoint(): void {
    if (!pointing.current) return;
    pointing.current = false;
    point(b.number, -1, -1);
  }

  const down = (e: PointerEvent) => {
    if (!draws || e.button > 0) return;
    const [x, y] = at(e);
    canvas.current!.setPointerCapture(e.pointerId);
    canvas.current!.focus({ preventScroll: true });
    const w = WIDTHS[width][0];
    switch (tool) {
      case "pen":
        drag.current = { kind: "pen" };
        return penDown(b.number, x, y, color, w);
      case "line":
      case "arrow":
      case "rect":
      case "ellipse":
        drag.current = { kind: "shape", shape: tool, x0: x, y0: y, x1: x, y1: y };
        return redraw((n) => n + 1);
      case "eraser": {
        const erased = new Set<string>();
        drag.current = { kind: "erase", erased };
        return eraseAt(x, y, erased);
      }
      case "hand": {
        const h = hit(shapesOf(b), x, y, reach());
        const ids = h === null ? [] : selected.includes(h.id) ? selected : [h.id];
        setPicked(ids);
        drag.current = ids.length > 0 ? { kind: "move", ids, x0: x, y0: y, dx: 0, dy: 0 } : null;
        return;
      }
      case "text":
        e.preventDefault();
        typed.current = false;
        return setTyping({ x, y });
      case "laser":
        return pointAt(x, y);
    }
  };

  const move = (e: PointerEvent) => {
    if (tool === "laser" && draws) {
      const [x, y] = at(e);
      return pointAt(x, y);
    }
    const d = drag.current;
    if (d === null) return;
    switch (d.kind) {
      case "pen":
        // The points the browser saw between two events too: a quick hand's line stays smooth.
        for (const c of e.getCoalescedEvents?.() ?? [e]) {
          const [x, y] = at(c);
          penTo(x, y);
        }
        return;
      case "shape": {
        [d.x1, d.y1] = at(e);
        return redraw((n) => n + 1);
      }
      case "move": {
        const [x, y] = at(e);
        [d.dx, d.dy] = [x - d.x0, y - d.y0];
        return redraw((n) => n + 1);
      }
      case "erase": {
        const [x, y] = at(e);
        return eraseAt(x, y, d.erased);
      }
    }
  };

  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (d === null) return;
    if (d.kind === "pen") return penUp();
    if (d.kind === "shape" && Math.hypot(d.x1 - d.x0, d.y1 - d.y0) >= LEAST)
      addShape(b.number, { ...dragged(d, color, WIDTHS[width][0]), id: newId() });
    if (d.kind === "move") moveShapes(b.number, d.ids, d.dx, d.dy);
    redraw((n) => n + 1);
  };

  const leave = () => unpoint();

  const key = (e: KeyboardEvent) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.code === "KeyZ") {
      e.preventDefault();
      return e.shiftKey ? redo(b.number) : undo(b.number);
    }
    if (mod && e.code === "KeyY") {
      e.preventDefault();
      return redo(b.number);
    }
    if ((e.key === "Delete" || e.key === "Backspace") && selected.length > 0 && draws) {
      e.preventDefault();
      eraseShapes(b.number, selected);
      return setPicked([]);
    }
    if (e.key === "Escape") setPicked([]);
  };

  /** The text written goes on the board — once: Enter, then the field losing focus, write it once. */
  const write = (text: string) => {
    const t = typing;
    if (t === null || typed.current) return;
    typed.current = true;
    setTyping(null);
    const words = text.replace(/\s+$/, "").slice(0, MOST_TEXT);
    if (words.trim() === "") return;
    addShape(b.number, {
      id: newId(),
      kind: "text",
      points: Int16Array.of(t.x, t.y),
      color,
      width: 1,
      text: words,
      size: WIDTHS[width][1],
      by: 0,
    });
  };

  const pick = (t: Tool) => {
    if (t !== "laser") unpoint();
    if (t !== "hand") setPicked([]);
    setTool(t);
  };

  const scale = box[0] / WIDTH;
  const own = b.author === state.user;
  return (
    <div class="board-tile" data-board={b.number}>
      <div class="board-head">
        <Icon name="board" size={16} />
        <span class="board-title">{own ? "Ваша доска" : `Доска: ${nameOf(b.author)}`}</span>
        {b.mode === "hosts" && (
          <span class="board-mode" title="Рисуют только владелец и модераторы">
            <Icon name="lock" size={12} />
            ведущие
          </span>
        )}
        <span class="board-acts">
          {hosts() && (
            <IconButton
              icon={b.mode === "all" ? "users" : "lock"}
              title={b.mode === "all" ? "Рисуют все. Сделать: только ведущие" : "Рисуют ведущие. Сделать: все"}
              onClick={() => setMode(b.number, b.mode === "all" ? "hosts" : "all")}
            />
          )}
          <IconButton icon="download" title="Сохранить картинкой (PNG)" onClick={() => save(b)} />
          {mayClose(b) && (
            <IconButton
              icon="trash"
              title="Очистить доску"
              onClick={() => {
                if (confirm("Очистить доску для всех?")) clearBoard(b.number);
              }}
            />
          )}
          {mayClose(b) && (
            <IconButton
              icon="close"
              title="Закрыть доску"
              onClick={() => {
                const sure = confirm("Закрыть доску для всех? Нарисованное пропадёт — сохраните его картинкой.");
                if (sure) closeBoard(b.number);
              }}
            />
          )}
        </span>
      </div>
      <div class="board-area" ref={area}>
        <div class="board-paper" style={{ width: `${box[0]}px`, height: `${box[1]}px` }}>
          <canvas
            ref={canvas}
            class={`tool-${draws ? tool : "none"}`}
            tabIndex={0}
            aria-label="Доска"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={leave}
            onKeyDown={key}
          />
          {typing !== null && (
            <textarea
              ref={field}
              class="board-text"
              maxLength={MOST_TEXT}
              placeholder="Текст"
              style={{
                left: `${typing.x * scale}px`,
                top: `${typing.y * scale}px`,
                fontSize: `${WIDTHS[width][1] * scale}px`,
                color: css(color),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  write(e.currentTarget.value);
                }
                if (e.key === "Escape") {
                  typed.current = true;
                  setTyping(null);
                }
              }}
              onBlur={(e) => write(e.currentTarget.value)}
            />
          )}
        </div>
      </div>
      {draws ? (
        <div class="board-tools">
          {TOOLS.map(([t, icon, title]) => (
            <IconButton
              key={t}
              icon={icon}
              title={title}
              class={tool === t ? "on" : ""}
              pressed={tool === t}
              onClick={() => pick(t)}
            />
          ))}
          <span class="board-sep" />
          <button
            class="swatch"
            style={{ background: css(color) }}
            title="Цвет"
            aria-label="Цвет"
            aria-expanded={palette}
            onClick={() => setPalette(!palette)}
          />
          {WIDTHS.map(([w, , title], i) => (
            <button
              key={w}
              class={i === width ? "thickness on" : "thickness"}
              title={title}
              aria-label={title}
              aria-pressed={i === width}
              onClick={() => setWidth(i)}
            >
              <span style={{ height: `${Math.max(2, w / 3)}px` }} />
            </button>
          ))}
          <span class="board-sep" />
          <IconButton icon="undo" title="Отменить (Ctrl+Z)" onClick={() => undo(b.number)} />
          <IconButton icon="redo" title="Повторить (Ctrl+Shift+Z)" onClick={() => redo(b.number)} />
        </div>
      ) : (
        <div class="board-note">Рисуют владелец и модераторы</div>
      )}
      {/* Over the row of tools, not in it: the row scrolls on a phone, and would cut it off. */}
      {draws && palette && (
        <div class="board-palette">
          {COLORS.map((c) => (
            <button
              key={c}
              class={c === color ? "swatch on" : "swatch"}
              style={{ background: css(c) }}
              title="Цвет"
              aria-label="Цвет"
              aria-pressed={c === color}
              onClick={() => {
                setColor(c);
                setPalette(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** A picture of board `b`, as large as the board's units, on white: the browser saves it. */
function save(b: Board): void {
  const c = document.createElement("canvas");
  [c.width, c.height] = [WIDTH, HEIGHT];
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  paint(ctx, shapesOf(b), 1);
  c.toBlob((blob) => {
    if (blob === null) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `доска-${b.number}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  }, "image/png");
}
